import * as React from 'react';
import invariant from '@core/lib/invariant';

import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Story } from 'core/models/story-manager';
import { AppFactory } from 'app/app-factory';
import { VocabListModel } from '@core/models/ui/vocab-list-model';
import { learnStoryPath } from 'components/nav/path-helpers';
import { notEmpty } from '@utils/conditionals';
import { styled } from '@naan/stitches.config';
import { VocabListHeading } from './vocab-list-heading';
import { VocabListSectionHeading } from './vocab-list-section-header';
import { VocabRow } from './vocab-list-row';
import { FullScreenError } from 'components/error-boundary';
import { FullScreenLoader } from 'components/ds/modals';
import { useVocabListData } from './use-vocablist-data';
import { VocabListZeroState } from './vocablist-zero-state';
import { bugsnagNotify } from '@app/notification-service';

const Wrapper = styled('div', {
  maxHeight: '70vh',
  overflowY: 'auto',
  marginBottom: '$4',

  '& > .vocab-rows': {
    // borderTop: '1px solid $gray-100',
    '& > * + *': {
      borderTop: '1px solid $gray-100',
    },
  },
});

Wrapper.displayName = 'VocabListWrapper';

const VocabList = observer(
  ({
    story,
    model,
    onDismiss,
  }: {
    model: VocabListModel;
    story: Story;
    onDismiss: () => void;
  }) => {
    const { progress } = story;
    const navigate = useNavigate();

    const applyVocabChanges = React.useCallback(() => {
      const { removalSlugs } = model;
      if (notEmpty(removalSlugs)) {
        progress.removeVocabs(removalSlugs);
        AppFactory.root.userManager.persistUserData().catch(bugsnagNotify);
      }
      navigate(learnStoryPath(story));
    }, [model, progress, navigate, story]);

    const handleDismiss = React.useCallback(() => {
      applyVocabChanges();
      onDismiss();
    }, [applyVocabChanges, onDismiss]);

    return (
      <>
        <VocabListHeading count={model.count} onClose={handleDismiss} />
        <Wrapper>
          {model.sections.map(section => (
            <React.Fragment key={section.chapterData.position}>
              <VocabListSectionHeading section={section} />
              <div className="vocab-rows">
                {section.rows.map(data => (
                  <VocabRow data={data} key={data.slug} />
                ))}
              </div>
            </React.Fragment>
          ))}
        </Wrapper>
      </>
    );
  }
);

export const VocabListModalContent = observer(
  ({ story, onDismiss }: { story: Story; onDismiss: () => void }) => {
    const { progress } = story;

    if (!progress.hasVocab) {
      return <VocabListZeroState onDismiss={onDismiss} />;
    }

    // log.debug('volumeDataUrl: ', story.volumeDataUrl);
    const { model, error, loading } = useVocabListData(
      story,
      progress.vocabList
    );

    if (loading) {
      return <FullScreenLoader />; // todo: confirm if we need any loading ui state
    }

    if (error) {
      invariant(false, error);
      // todo: ask frank about a different flavor error screen suitable when can still render enough to navigate away
      return <FullScreenError />;
    }

    if (model.volumeData == null) {
      return <FullScreenError message="No volume data" />;
    }

    return <VocabList model={model} story={story} onDismiss={onDismiss} />;
  }
);
